"use client";

import { useEffect, useState, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "@/components/ui/select";
import {
  ArrowUpRight,
  Check,
  X,
  ImagePlus,
  Loader2,
  Crop as CropIcon,
  ArrowLeft,
} from "lucide-react";
import useAxiosPrivate from "@/hooks/useAxiosPrivate";
import { useRouter } from "next/navigation";
import { useToast } from "@/components/custom/CustomToastProvider";
import ReactCrop from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";

/* -------------------------
   IMAGE CROP HELPER
-------------------------- */
async function getCroppedImg(image, crop) {
  const canvas = document.createElement("canvas");
  const scaleX = image.naturalWidth / image.width;
  const scaleY = image.naturalHeight / image.height;

  canvas.width = 1200;
  canvas.height = 1600;

  const ctx = canvas.getContext("2d");
  ctx.drawImage(
    image,
    crop.x * scaleX,
    crop.y * scaleY,
    crop.width * scaleX,
    crop.height * scaleY,
    0,
    0,
    canvas.width,
    canvas.height
  );

  return new Promise((resolve) => {
    canvas.toBlob((file) => resolve(file), "image/jpeg");
  });
}

/* -------------------------
   VALIDATION SCHEMA
-------------------------- */
const schema = z
  .object({
    name: z.string().optional(),
    phone: z.string().regex(/^\d{10}$/, "Invalid phone number"),
    email: z.string().email("Invalid email").optional(),
    pincode: z.string().regex(/^\d{6}$/, "Invalid pincode"),
    city: z.string().min(1, "City is required"),
    landmark: z.string().min(1, "Landmark is required"),
    addressLine: z.string().min(1, "Address Line is required"),
    projectStage: z.string().min(1, "Project Stage is required"),
    revenueType: z.enum(["revenueSharing", "fixedRent"]),
    revenuePercentage: z.string().optional(),
    fixedRent: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    // REQUIRED ONLY WHEN NEW USER
    if (!ctx?.parent?.isExistingUser) {
      if (!data.name || data.name.trim().length === 0) {
        ctx.addIssue({
          path: ["name"],
          code: z.ZodIssueCode.custom,
          message: "Name is required",
        });
      }

      if (!data.email || data.email.trim().length === 0) {
        ctx.addIssue({
          path: ["email"],
          code: z.ZodIssueCode.custom,
          message: "Email is required",
        });
      }
    }
    if (data.revenueType === "revenueSharing" && !data.revenuePercentage) {
      ctx.addIssue({
        path: ["revenuePercentage"],
        code: z.ZodIssueCode.custom,
        message: "Revenue percentage is required",
      });
    }
    if (data.revenueType === "fixedRent" && !data.fixedRent) {
      ctx.addIssue({
        path: ["fixedRent"],
        code: z.ZodIssueCode.custom,
        message: "Fixed rent amount is required",
      });
    }
  });
export default function CreateLocationModal({ open, onOpenChange }) {
  const [step, setStep] = useState(1);
  const [showContinue, setShowContinue] = useState(false);
  const [pincodeDataState, setPincodeDataState] = useState(null);

  const axiosPrivate = useAxiosPrivate();
  const router = useRouter();
  const toast = useToast();

  const [chargersList, setChargersList] = useState([]);
  const [selectedChargers, setSelectedChargers] = useState([]);

  const [imgSrc, setImgSrc] = useState("");
  const [crop, setCrop] = useState();
  const [completedCrop, setCompletedCrop] = useState(null);
  const [finalImages, setFinalImages] = useState([]);
  const [isCropping, setIsCropping] = useState(false);

  const imgRef = useRef(null);
  const fileInputRef = useRef(null);
  const {
    register,
    watch,
    setValue,
    trigger,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(schema),
    mode: "onChange",
    defaultValues: {
      name: "",
      phone: "",
      email: "",
      pincode: "",
      city: "",
      landmark: "",
      addressLine: "",
      revenueType: "revenueSharing",
      projectStage: "Survey",
    },
  });

  const phone = watch("phone");
  const pincode = watch("pincode");
  const revenueType = watch("revenueType");
  const projectStages = [
    "Survey",
    "Feasibility",
    "Manufacturing",
    "Installation",
    "Electrical",
    "Handover",
  ];
  const { data: fetchedUser } = useQuery({
    queryKey: ["get-user-by-phone", phone],
    queryFn: async () => {
      const res = await axiosPrivate.get(`/user/get-by-phone?phone=${phone}`);
      return res.data.data;
    },
    enabled: phone?.length === 10,
    staleTime: 0,
  });

  const isExistingUser = !!fetchedUser;
  useEffect(() => {
    if (!phone || phone.length !== 10) {
      setValue("name", "");
      setValue("email", "");
      return;
    }

    if (fetchedUser) {
      setValue("name", fetchedUser.name || "");
      setValue("email", fetchedUser.email || "");
    } else {
      setValue("name", "");
      setValue("email", "");
    }
  }, [phone, fetchedUser, setValue]);
  useEffect(() => {
    if (!open) return;

    const fetchChargers = async () => {
      try {
        const res = await axiosPrivate.get("/charger/charger-getall");
        // support several possible response shapes
        const records =
          res?.data?.data?.records ||
          res?.data?.records ||
          res?.data?.data ||
          res?.data?.chargers ||
          [];
        setChargersList(Array.isArray(records) ? records : []);
      } catch (err) {
        console.error("Failed to fetch chargers:", err);
        setChargersList([]);
      }
    };

    fetchChargers();
  }, [open, axiosPrivate]);
  const { refetch: fetchPincode } = useQuery({
    queryKey: ["get-district", pincode],
    queryFn: async () => {
      const res = await axiosPrivate.get(
        `/pincode/admin-getpincode?pincode=${pincode}`
      );
      const data = res.data.data;
      if (data?.district) {
        setShowContinue(true);
        setPincodeDataState(data);
      } else {
        setShowContinue(false);
        setPincodeDataState(null);
      }
      return data;
    },
    enabled: false,
  });

  useEffect(() => {
    if (pincode?.length === 6) fetchPincode();
    else {
      setShowContinue(false);
      setPincodeDataState(null);
    }
  }, [pincode, fetchPincode]);
  const handleNext = async () => {
    let fields = [];

    if (step === 1) {
      fields.push("phone");
      if (!isExistingUser) fields.push("name", "email");
    }

    if (step === 2) {
      fields = [
        "pincode",
        "city",
        "landmark",
        "addressLine",
        "revenueType",
        "projectStage",
      ];
    }

    const valid = await trigger(fields);
    if (valid) setStep(step + 1);
  };

  const handleChargerSelect = (value) => {
    if (!selectedChargers.includes(value)) {
      setSelectedChargers((prev) => [...prev, value]);
    }
  };

  const handleChargerRemove = (id) => {
    setSelectedChargers(selectedChargers.filter((x) => x !== id));
  };

  const mutation = useMutation({
    mutationFn: async (payload) => {
      const form = new FormData();
      const append = (k, v) =>
        v !== "" && v !== undefined && v !== null && form.append(k, v);

      append("userName", payload.userName);
      append("userPhoneNumber", payload.userPhoneNumber);
      append("userEmail", payload.userEmail);
      append("locationName", payload.locationName);
      append("addressLine", payload.addressLine);
      append("landmark", payload.landmark);
      append("pincode", payload.pincode);
      append("projectStage", payload.projectStage);
      append("revenueType", payload.revenueType);

      if (payload.revenueType === "revenueSharing")
        append("revenuePercentage", payload.revenuePercentage);
      if (payload.revenueType === "fixedRent")
        append("fixedRent", payload.fixedRent);

      payload.chargers.forEach((c) => form.append("chargers", c));
      payload.images.forEach((img, idx) =>
        form.append("images", img, `image-${idx}.jpg`)
      );

      return axiosPrivate.post(
        "/distributer-location/add-location",
        form,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
    },
    onSuccess: () => {
      toast.success("Location added successfully!");
      reset({
        name: "",
        phone: "",
        email: "",
        pincode: "",
        city: "",
        landmark: "",
        addressLine: "",
        revenueType: "revenueSharing",
        projectStage: "Survey",
      });

      setStep(1);
      setSelectedChargers([]);
      setFinalImages([]);
      setPincodeDataState(null);
      onOpenChange(false);
      router.push("/");
    },
    onError: (e) =>
      toast.error(e.response?.data?.message || "Submission failed"),
  });

  const onSelectFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      toast.error("File size too large!");
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setImgSrc(reader.result?.toString() || "");
      setCrop(undefined);
      setIsCropping(true);
    };
    reader.readAsDataURL(file);
  };

  const handleCropComplete = async () => {
    if (completedCrop?.width && imgRef.current) {
      const file = await getCroppedImg(imgRef.current, completedCrop);
      setFinalImages((prev) => [...prev, file]);
      setIsCropping(false);
      setImgSrc("");
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const onSubmit = (d) => {
    if (finalImages.length < 2) {
      toast.error("Upload at least 2 images.");
      return;
    }

    mutation.mutate({
      userName: d.name,
      userPhoneNumber: d.phone,
      userEmail: d.email,
      locationName: d.city,
      addressLine: d.addressLine,
      landmark: d.landmark,
      pincode: d.pincode,
      projectStage: d.projectStage,
      revenueType: d.revenueType,
      revenuePercentage: d.revenuePercentage,
      fixedRent: d.fixedRent,
      chargers: selectedChargers,
      images: finalImages,
    });
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="w-[95vw] max-w-[95vw] sm:max-w-[auto] md:max-w-[900px] lg:max-w-[1100px] xl:max-w-[1200px] lg:min-h-[90vh] sm:min-h-[auto] h-auto rounded-xl p-0 overflow-hidden lg:py-0 py-2">
          <div className="flex flex-col sm:flex-row w-full h-full">

            {/* Left Banner */}
            <div className="hidden relative w-full sm:w-1/2 text-white lg:flex flex-col justify-center items-center sm:p-10 min-h-[600px]">
              <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: "url('/modal-bg.png')" }}></div>
              {step < 4 && (
                <div className="relative z-10 space-y-4">
                  <div className="bg-green-600 text-white rounded-lg p-4 inline-block"><ArrowUpRight className="h-8 w-8" /></div>
                  <h2 className="text-5xl font-bold">Invest in <br /> Future.</h2>
                </div>
              )}
            </div>

            {/* Right Form */}
            <div className="w-full flex-1 lg:flex flex-col justify-center items-center sm:w-1/2 bg-white lg:px-8 lg:py-10 px-4 py-5 min-h-[600px] overflow-y-auto max-h-[90vh]">

              {/* STEP 1: Basic Details */}
              {step === 1 && (
                <div className="space-y-5 w-full max-w-md">
                  <DialogHeader><DialogTitle className="text-xl font-bold">Get Started!</DialogTitle></DialogHeader>
                  <div>
                    <Input placeholder="Phone Number" {...register("phone")} />
                    {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone.message}</p>}
                  </div>
                  <div>
                    <Input
                      placeholder="Name"
                      {...register("name")}
                      disabled={isExistingUser}
                      className={isExistingUser ? "bg-gray-100 cursor-not-allowed" : ""}
                    />
                    {errors.name && !isExistingUser && (
                      <p className="text-red-500 text-xs mt-1">{errors.name.message}</p>
                    )}
                  </div>

                  <Input
                    placeholder="Email"
                    {...register("email")}
                    disabled={isExistingUser}
                    className={isExistingUser ? "bg-gray-100 cursor-not-allowed" : ""}
                  />

                  {errors.email && !isExistingUser && (
                    <p className="text-red-500 text-xs mt-1">{errors.email.message}</p>
                  )}

                  <Button onClick={handleNext} className="w-full bg-green-600 hover:bg-green-700">Continue</Button>
                </div>
              )}

              {/* STEP 2: Location, Revenue & Chargers */}
              {step === 2 && (
                <form className="space-y-4 w-full max-w-md" onSubmit={(e) => { e.preventDefault(); handleNext(); }}>

                  {/* BACK ARROW STEP 2 (Goes to Step 1) */}
                  <div className="flex items-center w-full mb-2">
                    <Button variant="ghost" size="icon" type="button" onClick={() => setStep(1)} className="mr-2 -ml-3 h-8 w-8 rounded-full">
                      <ArrowLeft className="w-5 h-5 text-gray-600" />
                    </Button>
                    <DialogHeader className="p-0 space-y-0">
                      <DialogTitle className="text-xl font-bold">Location & Details</DialogTitle>
                    </DialogHeader>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Input placeholder="Pincode" {...register("pincode")} />
                      {errors.pincode && <p className="text-red-500 text-xs mt-1">{errors.pincode.message}</p>}
                    </div>
                    <div>
                      <Input placeholder="Town / City" {...register("city")} />
                      {errors.city && <p className="text-red-500 text-xs mt-1">{errors.city.message}</p>}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <Input placeholder="District" value={pincode && pincode.length === 6 ? pincodeDataState?.district ?? "" : ""} disabled />
                    <Input placeholder="State" value={pincode && pincode.length === 6 ? pincodeDataState?.statename ?? "" : ""} disabled />
                  </div>

                  <Input placeholder="House No / Street" {...register("addressLine")} />
                  {errors.addressLine && <p className="text-red-500 text-xs mt-1">{errors.addressLine.message}</p>}

                  <Input placeholder="Landmark" {...register("landmark")} />
                  {errors.landmark && <p className="text-red-500 text-xs mt-1">{errors.landmark.message}</p>}

                  {/* Project Stage */}
                  <div className="border-t pt-2 mt-2">
                    <label className="text-sm font-semibold mb-1 block">Project Stage</label>
                    <Controller
                      name="projectStage"
                      control={control}
                      render={({ field }) => (
                        <Select value={field.value} onValueChange={field.onChange}>
                          <SelectTrigger><span>{field.value || "Select Stage"}</span></SelectTrigger>
                          <SelectContent>
                            {projectStages.map(stage => (
                              <SelectItem key={stage} value={stage}>{stage}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    />
                    {errors.projectStage && <p className="text-red-500 text-xs">{errors.projectStage.message}</p>}
                  </div>

                  {/* Revenue Section */}
                  <div className="border-t pt-2 mt-2">
                    <label className="text-sm font-semibold mb-1 block">Revenue Model</label>
                    <Controller
                      name="revenueType"
                      control={control}
                      render={({ field }) => (
                        <Select value={field.value} onValueChange={field.onChange}>
                          <SelectTrigger><span>{field.value === 'fixedRent' ? 'Fixed Rent' : 'Revenue Sharing'}</span></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="revenueSharing">Revenue Sharing</SelectItem>
                            <SelectItem value="fixedRent">Fixed Rent</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                    />

                    <div className="grid grid-cols-2 gap-4 mt-2">
                      {revenueType === "revenueSharing" && (
                        <div>
                          <Input placeholder="% Share" {...register("revenuePercentage")} />
                          {errors.revenuePercentage && <p className="text-red-500 text-xs">{errors.revenuePercentage.message}</p>}
                        </div>
                      )}
                      {revenueType === "fixedRent" && (
                        <div>
                          <Input placeholder="Rent Amount" {...register("fixedRent")} />
                          {errors.fixedRent && <p className="text-red-500 text-xs">{errors.fixedRent.message}</p>}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Chargers Section */}
                  <div className="border-t pt-2 mt-2">
                    <label className="text-sm font-semibold mb-1 block">Select Chargers</label>
                    <Select onValueChange={handleChargerSelect}>
                      <SelectTrigger><span>Add Charger</span></SelectTrigger>
                      <SelectContent>
                        {chargersList.map(c => (
                          <SelectItem key={c._id} value={c._id}>{c.title}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {selectedChargers.map(id => {
                        const c = chargersList.find(x => x._id === id);
                        return (
                          <div key={id} className="flex items-center gap-1 bg-gray-100 px-2 py-1 rounded text-xs">
                            {c?.title} <X className="w-3 h-3 cursor-pointer" onClick={() => handleChargerRemove(id)} />
                          </div>
                        )
                      })}
                    </div>
                  </div>

                  <Button type="submit" disabled={!showContinue} className="w-full bg-green-600 hover:bg-green-700 disabled:bg-gray-400 mt-4">Continue</Button>
                </form>
              )}

              {/* STEP 3: Images */}
              {step === 3 && (
                <div className="space-y-4 w-full h-full flex flex-col justify-start pt-10">

                  {/* BACK ARROW STEP 3 (Goes to Step 2) */}
                  <div className="flex items-center w-full mb-6">
                    <Button variant="ghost" size="icon" onClick={() => setStep(2)} className="mr-2 -ml-3 h-8 w-8 rounded-full">
                      <ArrowLeft className="w-5 h-5 text-gray-600" />
                    </Button>
                    <DialogHeader className="p-0 space-y-0">
                      <DialogTitle className="text-xl font-bold">Upload Photos</DialogTitle>
                      <p className="text-sm text-gray-500 text-left">Upload at least 2 photos (3:4 Portrait).</p>
                    </DialogHeader>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    {/* Preview Uploaded Images */}
                    {finalImages.map((blob, idx) => (
                      <div key={idx} className="relative aspect-[3/4] bg-gray-100 rounded-lg overflow-hidden border">
                        <img src={URL.createObjectURL(blob)} alt={`cropped-${idx}`} className="w-full h-full object-cover" />
                        <button onClick={() => setFinalImages(prev => prev.filter((_, i) => i !== idx))} className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full"><X className="w-4 h-4" /></button>
                      </div>
                    ))}

                    {/* Upload Button - HIDDEN IF 2 OR MORE IMAGES */}
                    {finalImages.length < 2 && (
                      <div className="relative aspect-[3/4] bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg flex flex-col justify-center items-center cursor-pointer hover:bg-gray-100 transition">
                        <ImagePlus className="w-8 h-8 text-gray-400 mb-2" />
                        <span className="text-xs text-gray-500 text-center px-2">Click to Upload</span>
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/*"
                          onChange={onSelectFile}
                          className="absolute inset-0 opacity-0 cursor-pointer"
                        />
                      </div>
                    )}
                  </div>

                  <Button onClick={handleSubmit(onSubmit)} disabled={finalImages.length < 2 || mutation.isPending} className="w-full bg-green-600 hover:bg-green-700 mt-5">
                    {mutation.isPending ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Submitting...</> : "Submit Application"}
                  </Button>
                </div>
              )}

              {/* STEP 4: Success */}
              {step === 4 && (
                <div className="text-center space-y-5">
                  <div className="flex justify-center"><div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center"><Check className="w-8 h-8 text-green-600" /></div></div>
                  <h2 className="text-xl font-bold">Location Added!</h2>
                  <Button onClick={() => onOpenChange(false)} className="w-full bg-green-600 hover:bg-green-700">Close</Button>
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* CROP DIALOG */}
      <Dialog open={isCropping} onOpenChange={setIsCropping}>
        <DialogContent className="max-w-3xl h-[90vh] flex flex-col p-6">
          <DialogHeader><DialogTitle>Crop Image (3:4 Portrait)</DialogTitle></DialogHeader>
          <div className="flex-1 overflow-hidden bg-slate-100 flex items-center justify-center border rounded-md">
            {imgSrc && (
              <ReactCrop
                crop={crop}
                onChange={(_, c) => setCrop(c)}
                onComplete={(c) => setCompletedCrop(c)}
                aspect={3 / 4}
                className="max-h-[60vh]"
              >
                <img ref={imgRef} alt="Crop me" src={imgSrc} className="max-h-[60vh] w-auto object-contain" />
              </ReactCrop>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCropping(false)}>Cancel</Button>
            <Button onClick={handleCropComplete}><CropIcon className="w-4 h-4 mr-2" /> Crop & Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
